<script lang="ts" setup>
import type { SceneObjectSettings } from '~/models/scene.js'
import type { ContentSvg } from '~/models/Content/ContentSvg.js'
import type { ContentSceneObject } from '~/models/Content/ContentSceneObject'
import type { ContentScene } from '~/models/Content/ContentScene'
import type { ContentImage } from '~/models/Content/ContentImage.js'
import { computed, defineAsyncComponent, nextTick, reactive, ref, shallowRef, useTemplateRef } from 'vue'
import { storeToRefs } from 'pinia'
import { useAsyncState, useDebounceFn } from '@vueuse/core'
import { useQuery } from '@tanstack/vue-query'
import { faAngle, faArrowPointer, faClone, faInfo, faPlusLarge, faQuestion, faRedo, faRulerHorizontal, faTrash, faTrashXmark, faUndo } from '@fortawesome/duotone-regular-svg-icons'
import useAuthStore from '~/stores/auth.js'
import { PendoFeature, PendoTrackName } from '~/models/Pendo.js'
import { Subtree } from '~/models/Content/Subtree'
import { ContentType } from '~/models/Content/ContentType'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import usePendo from '~/composables/usePendo.js'
import { useContentHelper } from '~/composables/useContentHelper.js'
import useMetadataApi from '~/api/metadataApi.js'
import useContentApi from '~/api/contentApi'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'
import Button from '~/components/ui/Button.vue'
import AuSwitch from '~/components/ui/AuSwitch.vue'
import AuDialog from '~/components/ui/AuDialog.vue'
import Image from '~/components/media/Image.vue'
import ArticleTeacherGuide from '~/components/article/ArticleTeacherGuide.vue'

const props = defineProps<{
  contentId: number
} | {
  content: ContentScene
}>()

const SceneCanvas = defineAsyncComponent(() => import('~/components/scene/SceneCanvas.vue'))
const SceneObject = defineAsyncComponent(() => import('~/components/scene/SceneObject.vue'))

const { buildSvgUrlByField } = useUrlBuilder()
const { isSvg, isImage } = useContentHelper()
const { findContents } = useContentApi()
const { pendoTrack, pendoFlushNow } = usePendo()
const { isTeacher, userId, isAuthenticated } = storeToRefs(useAuthStore())
const metadata = useMetadataApi()

const contentId = computed(() => 'content' in props && props.content ? props.content.contentId : (props as { contentId: number }).contentId)

const scene = useQuery({
  queryKey: ['scene', contentId],
  staleTime: Infinity,
  async queryFn() {
    const [content] = 'content' in props && props.content
      ? [props.content]
      : await findContents<ContentScene>({
          contentTypeCriterion: [ContentType.Scene],
          subtreeCriterion: [Subtree.Content, Subtree.Media],
          contentIdCriterion: [contentId.value],
          mainLocationCriterion: true,
        }, 1)

    return content
  }
})

const sceneObjects = useQuery({
  queryKey: ['scene-objects', contentId],
  initialData: { locations: [], images: [] },
  initialDataUpdatedAt: 0,
  staleTime: 1000 * 60 * 60 * 12,
  enabled: computed(() => isAuthenticated.value && scene.data.value != null),
  async queryFn() {
    if (!scene.data.value)
      return { locations: [], images: [] }

    const locations = (await findContents<ContentSceneObject>({
      contentTypeCriterion: [ContentType.SceneObject],
      subtreeCriterion: [Subtree.Content, Subtree.Media],
      mainLocationCriterion: true,
      contentIdCriterion: [...new Set([
        ...scene.data.value.availableObjects.destinationContentIds,
        ...scene.data.value.placedObjects.destinationContentIds,
      ])]
    })).filter(({ image }) => image.destinationContentId)

    const images = await findContents<ContentImage | ContentSvg>({
      contentTypeCriterion: [ContentType.Image, ContentType.Svg],
      contentIdCriterion: locations.map(({ image }) => Number(image.destinationContentId)),
      mainLocationCriterion: true,
      subtreeCriterion: [Subtree.Content, Subtree.Media],
    }, locations.length)

    return { locations, images }
  },
})

const saved = useAsyncState(isAuthenticated.value
  ? metadata.get<any[]>(`scene-${contentId.value}`)
  : Promise.resolve([]), [])

const save = useDebounceFn(data => metadata.set(`scene-${contentId.value}`, data, userId.value), 1000)

const availableObjects = computed(() =>
  scene
    .data
    .value
    ?.availableObjects
    .destinationContentIds
    .map(contentId => sceneObjects.data.value.locations.find(location => location.contentId === contentId))
    .filter(obj => obj !== undefined)
    ?? []
)

const placedObjects = computed(() =>
  scene
    .data
    .value
    ?.placedObjects
    .destinationContentIds
    .map(contentId => sceneObjects.data.value.locations.find(location => location.contentId === contentId))
    .filter(obj => obj !== undefined)
    .reverse()
    ?? []
)

const savedObjects = computed((): ContentSceneObject[] =>
  saved
    .state
    .value
    ?.map((data) => {
      const object = sceneObjects.data.value.locations.find(location => location.contentId === data.contentId)
      return object
        ? {
            ...object,
            x: data.x,
            y: data.y,
            angle: data.angle,
            width: data.width,
          }
        : undefined
    })
    .filter(obj => obj !== undefined)
    ?? []
)

const addedObjects = reactive<ContentSceneObject[]>([])
const isGuideOpen = ref(false)
const isTeacherGuideOpen = ref(false)
const isMeasurementsVisible = ref(false)
const isResetSceneOpen = ref(false)

async function resetScene() {
  save([])
  saved.state.value = []
  addedObjects.splice(0)
  isResetSceneOpen.value = false
  await nextTick()
  sceneEl.value?.reset()
}

function createImageUrl(image: ContentImage | ContentSvg) {
  if (isSvg(image)) {
    return buildSvgUrlByField(image.contentId)
  }

  if (isImage(image)) {
    return image.image.uri
  }

  throw new TypeError('No supported image type')
}

async function clone(sceneObject: SceneObjectSettings) {
  const content = sceneObjects.data.value.locations.find(({ contentId }) => contentId === sceneObject.attr['data-content-id'])
  if (content) {
    addedObjects.push({
      ...content,
      x: sceneObject.values.x + 10,
      y: sceneObject.values.y + 10,
      angle: sceneObject.values.a,
      width: sceneObject.values.w,
    })
  }
}

function onClickCreate(content: ContentSceneObject) {
  creatingDragging.value = undefined
  addedObjects.push(content)
}

function onDragCreate(content: ContentSceneObject) {
  creatingDragging.value = content
  addedObjects.push(content)
  pendoTrack(PendoTrackName.SceneDragAction, {
    title: scene.data.value?.title ?? document.title,
    url: window.location.href,
    action: 'create',
    contentId: content.contentId,
  })
  pendoFlushNow()
}

function onDragDelete(obj: SceneObjectSettings) {
  sceneEl.value?.remove(obj)
  pendoTrack(PendoTrackName.SceneDragAction, {
    title: scene.data.value?.title ?? document.title,
    url: window.location.href,
    action: 'delete',
  })
  pendoFlushNow()
}

function getAmountLeft(content: ContentSceneObject) {
  if (!content.maxAmount)
    return Infinity

  return content.maxAmount - objects.value.reduce((n, { contentId }) => contentId === content.contentId ? n + 1 : n, 0)
}

function findSceneObjectLocation(contentId: number) {
  return sceneObjects.data.value.locations.find(obj => obj.contentId === contentId)
}

function findSceneObjectImage(contentId: number) {
  return sceneObjects.data.value.images.find(obj => obj.contentId === contentId)
}

const sceneEl = useTemplateRef('sceneel')

function onChange(objs: ReadonlySet<SceneObjectSettings>) {
  save(Array
    .from(objs)
    .filter(({ container }) => container.value?.visible)
    .map(obj => ({
      contentId: obj.attr['data-content-id'],
      x: obj.values.x,
      y: obj.values.y,
      angle: obj.values.a,
      width: obj.values.w,
    })))
}

const dragging = shallowRef<SceneObjectSettings>()
const creatingDragging = shallowRef<ContentSceneObject>()
const objects = computed(() => (savedObjects.value.length ? savedObjects.value : placedObjects.value).concat(addedObjects))
</script>

<template>
  <div class="relative content-center text-(--theme-5)" data-ignore-tts>
    <SceneCanvas
      v-if="scene.data.value && sceneObjects.isFetched && !saved.isLoading.value"
      ref="sceneel"
      :background="scene.data.value?.backgroundColor"
      class="m-auto max-h-full rounded-xl border border-(--theme-20)"
      @change="onChange"
    >
      <template #default="{ active }">
        <SceneObject
          v-for="(obj, index) in objects"
          :key="`${index}:${obj.contentId}`"
          :angle="obj.angle"
          :data-content-id="obj.contentId"
          :movable="!obj.static && obj.movable"
          :resizable="!obj.static && obj.resizable"
          :rotatable="!obj.static && obj.rotatable"
          :src="createImageUrl(findSceneObjectImage(Number(obj.image.destinationContentId))!)"
          :start-dragging="obj === creatingDragging"
          :title="obj.title"
          :width="obj.width"
          :x="obj.x"
          :y="obj.y"
          @dragend="dragging = undefined"
          @dragstart="dragging = $event"
        />

        <div
          v-if="active"
          :class="{ '!bg-coral-40 hover:!bg-coral-pop min-w-72 !text-white': dragging }"
          class="absolute top-3 left-1/2 flex -translate-x-1/2 gap-3 rounded-xl bg-(--theme-50) p-2"
          @pointerdown.prevent
        >
          <Button
            v-if="getAmountLeft(findSceneObjectLocation(Number(active.attr['data-content-id']))!) > 0"
            :aria-label="$t('scene.clone')"
            :class="{ invisible: dragging }"
            :data-pendo="PendoFeature.Scene.Clone"
            class="!transition-none"
            shape="square"
            size="lg"
            @click="clone(active)"
          >
            <font-awesome-icon :icon="faClone" />
          </Button>

          <Button
            :aria-label="$t('scene.delete')"
            :class="{ invisible: dragging }"
            :data-pendo="PendoFeature.Scene.ClickDelete"
            class="!transition-none"
            shape="square"
            size="lg"
            @click="sceneEl?.remove(active)"
          >
            <font-awesome-icon :icon="faTrash" />
          </Button>

          <span
            v-show="dragging"
            class="absolute inset-0 flex items-center justify-center gap-4 select-none"
            @pointerup="onDragDelete(active)"
          >
            <font-awesome-icon :icon="faTrash" />
            <span v-text="$t('scene.dragDelete')" />
          </span>
        </div>
      </template>

      <template v-if="isMeasurementsVisible" #position="{ pointer, active }">
        <div class="pointer-events-none absolute bottom-1 left-1 flex items-center gap-2 self-center rounded-lg bg-(--theme-50) p-2 tabular-nums">
          <font-awesome-icon :icon="faArrowPointer" />
          <span><span class="text-(--theme-20)">X</span>{{ Math.round(pointer.x) }}, <span class="text-(--theme-20)">Y</span>{{ Math.round(pointer.y) }}</span>
          <template v-if="active">
            <font-awesome-icon :icon="faPlusLarge" />
            <span><span class="text-(--theme-20)">X</span>{{ Math.round(active.values.x) }}, <span class="text-(--theme-20)">Y</span>{{ Math.round(active.values.y) }}</span>
            <font-awesome-icon :icon="faRulerHorizontal" />
            {{ Math.round(active.values.w) }}
            <font-awesome-icon :icon="faAngle" />
            {{ Math.round(active.values.a) }}&deg;
          </template>
        </div>
      </template>
    </SceneCanvas>

    <div class="absolute grid w-max auto-cols-fr auto-rows-fr justify-center gap-2 overflow-hidden rounded-lg bg-(--theme-50) p-2 portrait:top-2 portrait:left-1/2 portrait:max-w-full portrait:-translate-x-1/2 portrait:grid-flow-col landscape:top-1/2 landscape:left-2 landscape:max-h-[80lvh] landscape:-translate-y-1/2">
      <Button
        :aria-label="$t('scene.reset')"
        shape="square"
        size="lg"
        @click="isResetSceneOpen = true"
      >
        <font-awesome-icon :icon="faTrashXmark" />
      </Button>

      <Button
        :aria-label="$t('scene.reset')"
        shape="square"
        size="lg"
        @click="sceneEl?.undo()"
      >
        <font-awesome-icon :icon="faUndo" />
      </Button>

      <Button
        :aria-label="$t('scene.reset')"
        shape="square"
        size="lg"
        @click="sceneEl?.redo()"
      >
        <font-awesome-icon :icon="faRedo" />
      </Button>

      <AuDialog
        :open="isResetSceneOpen"
        :title="$t('scene.reset')"
        @update:open="isResetSceneOpen = false"
      >
        <template #content>
          <p class="mb-6" v-text="$t('scene.resetText')" />
          <Button
            class="float-end"
            :data-pendo="PendoFeature.Scene.Reset"
            @click="resetScene"
          >
            {{ $t('scene.resetConfirm') }}
          </Button>
        </template>
      </AuDialog>

      <Button
        :aria-label="$t('help')"
        :data-pendo="PendoFeature.Scene.Help"
        shape="square"
        size="lg"
        @click="isGuideOpen = !isGuideOpen"
      >
        <font-awesome-icon :icon="faQuestion" />
      </Button>

      <AuDialog
        :open="isGuideOpen"
        :title="$t('help')"
        size="lg"
        @update:open="isGuideOpen = false"
      >
        <template #content>
          <article class="prose prose-au prose-img:rounded-xl mx-auto">
            <RichTextRenderer v-if="scene.data.value?.studentGuide" :text="scene.data.value.studentGuide" />

            <h2 v-text="$t('scene.keyboard')" />

            <ul>
              <li v-text="$t('scene.keyboardMove')" />
              <li v-text="$t('scene.keyboardResize')" />
              <li v-text="$t('scene.keyboardRotate')" />
            </ul>

            <h2 v-text="$t('scene.settings')" />

            <label class="flex max-w-fit items-center gap-2">
              <AuSwitch v-model="isMeasurementsVisible" />
              {{ $t('scene.showMeasurements') }}
            </label>
          </article>
        </template>
      </AuDialog>

      <template
        v-if="isTeacher && scene.data.value?.teacherContent?.destinationContentId"
      >
        <Button
          :aria-label="$t('presentation.teacherGuide')"
          shape="square"
          size="lg"
          @click="isTeacherGuideOpen = !isTeacherGuideOpen"
        >
          <font-awesome-icon :icon="faInfo" />
        </Button>

        <ArticleTeacherGuide
          :content-id="scene.data.value.teacherContent.destinationContentId"
          :is-open="isTeacherGuideOpen"
          @close-guide="isTeacherGuideOpen = false"
        />
      </template>
    </div>

    <div class="absolute grid w-max auto-cols-fr auto-rows-fr gap-2 overflow-hidden rounded-xl bg-(--theme-50) p-2 portrait:bottom-2 portrait:left-1/2 portrait:max-w-full portrait:-translate-x-1/2 portrait:grid-flow-col landscape:top-1/2 landscape:right-2 landscape:max-h-[80lvh] landscape:-translate-y-1/2">
      <button
        v-for="{ obj, amount } in availableObjects.map((obj) => ({ obj, amount: getAmountLeft(obj) }))"
        :key="obj.locationId"
        :class="{ 'pointer-events-none': amount <= 0 }"
        :data-pendo="PendoFeature.Scene.ClickCreate"
        class="relative grid max-w-32 touch-none grid-cols-1 grid-rows-[minmax(0,1fr)_max-content] gap-2 focus-visible:ring"
        draggable="true"
        @click="(amount > 0) && onClickCreate(obj)"
        @dragstart.prevent="(amount > 0) && onDragCreate(obj)"
      >
        <div class="pointer-events-none grid max-h-full min-h-0 max-w-full min-w-0 grid-cols-1 grid-rows-1 place-content-center overflow-hidden rounded-lg border border-(--theme-10) p-2 text-(--theme-5)">
          <Image
            :content="Number(obj.image.destinationContentId)"
            :style="{ rotate: `${obj.angle}deg` }"
            class="aspect-square !h-full min-h-0 !w-full min-w-0 object-contain"
            width="128px"
          />
        </div>

        <span
          v-if="amount !== Infinity"
          :data-amount="amount"
          class="pointer-events-none absolute top-2 right-2 rounded-full bg-(--theme-10) px-1 text-(--theme-60) before:content-[attr(data-amount)] [&[data-amount='0']]:opacity-50"
        />

        <span class="pointer-events-none">{{ obj.title }}</span>
      </button>
    </div>
  </div>
</template>
